import type { PathKind } from "../domain/drill";
import { renderPathGeometry } from "../lib/path-rendering";

const lines: Array<{ kind: PathKind; label: string }> = [
  { kind: "skate", label: "Forward skating" },
  { kind: "skatePuck", label: "Skating with puck" },
  { kind: "backward", label: "Backward skating" },
  { kind: "backwardPuck", label: "Backward skating with puck" },
  { kind: "lateral", label: "Lateral skating" },
  { kind: "pass", label: "Pass" },
  { kind: "shot", label: "Shot" },
  { kind: "pressure", label: "Checking pressure" },
  { kind: "stop", label: "Stop" },
  { kind: "direction", label: "Direction" },
  { kind: "pivot", label: "Pivot" },
];

function LinePreview({ kind }: { kind: PathKind }) {
  const geometry = renderPathGeometry(kind, [
    { x: 6, y: 16 },
    { x: 72, y: 16 },
  ]);
  const hasArrow = kind !== "pressure" && kind !== "stop";
  return (
    <svg className="legend-line-preview" viewBox="0 0 82 32" aria-hidden="true">
      <defs>
        <marker
          id={`legend-arrow-${kind}`}
          markerWidth="7"
          markerHeight="7"
          refX="6"
          refY="3"
          orient="auto"
        >
          <path d="M0 0v6l7-3Z" />
        </marker>
      </defs>
      <path
        d={geometry.primary}
        strokeDasharray={geometry.dash}
        markerEnd={hasArrow ? `url(#legend-arrow-${kind})` : undefined}
      />
      {geometry.secondary && <path d={geometry.secondary} />}
      {geometry.ticks && <path d={geometry.ticks} />}
      {(kind === "pressure" || kind === "stop") && (
        <path d="M72 8V24" className="legend-end-mark" />
      )}
    </svg>
  );
}

export function DrillSymbolLegend({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="legend-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="legend-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legend-title"
      >
        <header>
          <div>
            <p className="eyebrow">Drawing reference</p>
            <h2 id="legend-title">Drill symbols</h2>
          </div>
          <button onClick={onClose} aria-label="Close symbol legend">
            ×
          </button>
        </header>
        <p className="legend-intro">
          Puckly uses familiar international hockey-diagram conventions. Colors
          identify groups; line patterns identify movement or puck action.
        </p>
        <div className="legend-grid">
          <div className="legend-section">
            <h3>People & equipment</h3>
            <div className="legend-row">
              <span className="legend-player">F</span>
              <span>Forward / player</span>
            </div>
            <div className="legend-row">
              <span className="legend-defender">D</span>
              <span>Defender / player</span>
            </div>
            <div className="legend-row">
              <span className="legend-coach">C</span>
              <span>Coach</span>
            </div>
            <div className="legend-row">
              <span className="legend-goalie">G</span>
              <span>Goaltender</span>
            </div>
            <div className="legend-row">
              <span className="legend-puck">●</span>
              <span>Puck / cluster of pucks</span>
            </div>
            <div className="legend-row">
              <span className="legend-cone">▲</span>
              <span>Cone or pylon</span>
            </div>
          </div>
          <div className="legend-section">
            <h3>Movement & actions</h3>
            {lines.map((line) => (
              <div className="legend-row" key={line.kind}>
                <LinePreview kind={line.kind} />
                <span>{line.label}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="legend-note">
          These are drawing conventions, not official playing-rule markings. Add
          instructions whenever a diagram could be interpreted in more than one
          way.
        </p>
      </section>
    </div>
  );
}
